import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { Inbox } from 'lucide-react';
import { TicketKanbanSkeleton } from '../../tickets/TicketListSkeleton';
import {
  getStatusLabel,
  getStatusFilterOptions,
  getStatusDefinition,
  getStatusClass,
  appliesToDemandType,
  isTerminalStatus,
  isReopenTargetStatus,
  requiresResolutionCapture,
} from '../../../services/statusService';
import { getDemandTypeLabel } from '../../../services/demandTypeService';
import { useApp } from '../../../context/AppContext';
import TicketCard from '../../tickets/TicketCard';
import ResolveTicketModal from './ResolveTicketModal';

const MOBILE_MQ = '(max-width: 768px)';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(MOBILE_MQ).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia(MOBILE_MQ);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return isMobile;
};

const hideScrollbarCss = `
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const thinScrollbarCss = `
  scrollbar-width: thin;
  scrollbar-color: var(--color-border-secondary) transparent;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--color-border-secondary);
    border-radius: 999px;
    border: 2px solid transparent;
    background-clip: content-box;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--color-border-accent);
    background-clip: content-box;
  }
`;

const KanbanViewport = styled.div`
  margin-top: 1rem;
  height: calc(100vh - 300px);
  min-height: 420px;
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;
  border-radius: 12px;
  scroll-behavior: smooth;
  ${thinScrollbarCss}
`;

const Board = styled.div`
  display: flex;
  gap: 0.75rem;
  height: 100%;
  padding: 0 0 0.75rem;
  box-sizing: border-box;
  align-items: stretch;
  width: max-content;
  min-width: 100%;
`;

const Column = styled.section`
  flex: 0 0 296px;
  max-width: 296px;
  min-height: 0;
  background: var(--color-bg-secondary);
  border-radius: 12px;
  border: 1px solid var(--color-border-primary);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: box-shadow 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;

  ${({ $isDragOver }) =>
    $isDragOver &&
    css`
      border-color: var(--color-text-primary);
      box-shadow: 0 0 0 3px var(--focus-ring);
    `}

  ${({ $isInvalidDrop }) =>
    $isInvalidDrop &&
    css`
      opacity: 0.45;
    `}
`;

const ColumnHeader = styled.header`
  padding: 0.75rem 0.875rem 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--color-bg-secondary);
  flex-shrink: 0;
`;

const StatusDot = styled.i`
  width: 8px;
  height: 8px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${({ $status }) => `var(--badge-status-${$status}-fg)`};
`;

const ColumnTitle = styled.h3`
  margin: 0;
  flex: 1;
  min-width: 0;
  font-weight: 600;
  font-size: 0.8125rem;
  color: var(--color-text-primary);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ColumnCount = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--color-text-muted);
  font-variant-numeric: tabular-nums;
`;

const ColumnBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  padding: 0.25rem 0.375rem 0.5rem 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior-y: contain;
  scrollbar-gutter: stable;
  transition: background-color 0.15s ease;
  min-width: 0;
  ${thinScrollbarCss}

  > * {
    flex-shrink: 0;
  }

  ${({ $isDragOver }) =>
    $isDragOver &&
    css`
      background: var(--color-hover-overlay);
    `}
`;

const ColumnEmpty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 72px;
  border: 1px dashed var(--color-border-secondary);
  border-radius: 10px;
  font-size: 0.75rem;
  color: var(--color-text-muted);

  ${({ $active }) =>
    $active &&
    css`
      border-color: var(--color-text-primary);
      color: var(--color-text-primary);
    `}
`;

const MobileKanban = styled.div`
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: calc(100vh - 220px);
  min-height: 0;
  overflow: hidden;
`;

const MobileTicketList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  flex: 1 1 auto;
  min-height: 0;
  padding: 0.15rem 0.1rem 0.5rem;
  ${hideScrollbarCss}
`;

const StatusTabs = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 0.5rem;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  touch-action: pan-x;
  cursor: grab;
  flex-shrink: 0;
  padding: 0.45rem 0.15rem 0.5rem;
  margin: 0;
  box-sizing: border-box;
  min-height: 3rem;
  ${hideScrollbarCss}

  &:active {
    cursor: grabbing;
  }
`;

const StatusTab = styled.button`
  appearance: none;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.875rem;
  border-radius: 999px;
  border: 1px solid
    ${({ $active }) => ($active ? 'var(--color-btn-primary-border)' : 'var(--color-border-primary)')};
  background: ${({ $active }) =>
    $active ? 'var(--color-btn-primary-bg)' : 'var(--color-bg-primary)'};
  color: ${({ $active }) =>
    $active ? 'var(--color-btn-primary-text)' : 'var(--color-text-secondary)'};
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  touch-action: manipulation;

  &:hover {
    color: ${({ $active }) =>
      $active ? 'var(--color-btn-primary-text)' : 'var(--color-text-primary)'};
  }
`;

const StatusTabCount = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  opacity: 0.7;
  font-variant-numeric: tabular-nums;
`;

const MobileEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  margin: 1rem 0;
  padding: 2rem 1rem;
  border: 1px dashed var(--color-border-secondary);
  border-radius: 12px;
  text-align: center;
  font-size: 0.875rem;
  color: var(--color-text-muted);
`;

const EmptyWrap = styled(MobileEmpty)`
  margin: 1rem 0 0;
  padding: 3rem 1rem;

  svg {
    color: var(--color-border-accent);
  }
`;

const canDropTicketInColumn = (ticket, columnCode) => {
  if (!ticket?.id || columnCode === '_OTHER') return false;
  // Ticket fechado só pode ir para status de pipeline aberto (reopen)
  if (isTerminalStatus(ticket.status) && !isReopenTargetStatus(columnCode)) {
    return false;
  }
  const statusDef = getStatusDefinition(columnCode);
  if (!statusDef) return true;
  return appliesToDemandType(statusDef, ticket.demandType);
};

const TicketKanbanView = ({
  tickets = [],
  loading = false,
  demandTypeFilter = null,
  statusFilter = [],
  detailsPath = '/all-tickets',
  listReturnPath = '/all-tickets',
  readOnly = false,
}) => {
  const { moveKanbanTicketStatus, addToast, users } = useApp();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const isDraggingRef = useRef(false);
  const pendingMoveRef = useRef(null);
  const dragFlushTimerRef = useRef(null);
  const docDragEndRef = useRef(null);
  const tabRefs = useRef({});
  const tabsScrollRef = useRef(null);
  const viewportRef = useRef(null);
  const autoScrollRafRef = useRef(null);
  const autoScrollPointerRef = useRef({ x: 0, y: 0, active: false });
  const tabsPointerDragRef = useRef({ active: false, startX: 0, scrollLeft: 0, moved: false });
  const listSwipeRef = useRef({ active: false, startX: 0, startY: 0 });
  const [draggedTicketId, setDraggedTicketId] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [updatingTicketIds, setUpdatingTicketIds] = useState(() => new Set());
  const [activeStatusCode, setActiveStatusCode] = useState(null);
  const [resolveModalPending, setResolveModalPending] = useState(null);
  const [resolveSubmitting, setResolveSubmitting] = useState(false);

  const draggedTicket = useMemo(
    () => tickets.find((ticket) => ticket.id === draggedTicketId) || null,
    [tickets, draggedTicketId]
  );

  const columns = useMemo(() => {
    let statusOptions = getStatusFilterOptions(demandTypeFilter);
    const activeStatusFilter = Array.isArray(statusFilter)
      ? statusFilter.map((s) => String(s).toUpperCase()).filter(Boolean)
      : [];

    if (activeStatusFilter.length > 0) {
      const selectedSet = new Set(activeStatusFilter);
      statusOptions = statusOptions.filter((opt) =>
        selectedSet.has(String(opt.value).toUpperCase())
      );
    }

    const knownCodes = new Set(statusOptions.map((o) => String(o.value).toUpperCase()));

    const grouped = new Map();
    statusOptions.forEach((opt) => {
      grouped.set(String(opt.value).toUpperCase(), []);
    });

    const unknown = [];

    tickets.forEach((ticket) => {
      const code = String(ticket.status || '').toUpperCase();
      if (knownCodes.has(code)) {
        grouped.get(code).push(ticket);
      } else if (code) {
        unknown.push(ticket);
      } else {
        unknown.push(ticket);
      }
    });

    const result = statusOptions.map((opt) => ({
      code: String(opt.value).toUpperCase(),
      label: opt.label,
      tickets: grouped.get(String(opt.value).toUpperCase()) || [],
    }));

    if (unknown.length > 0 && activeStatusFilter.length === 0) {
      result.push({
        code: '_OTHER',
        label: 'Outros',
        tickets: unknown,
      });
    }

    return result;
  }, [tickets, demandTypeFilter, statusFilter]);

  const visibleColumns = useMemo(() => {
    const filtered = columns.filter((column) => {
      if (column.code !== '_OTHER') return true;
      return column.tickets.length > 0;
    });
    const withTickets = filtered.filter((column) => column.tickets.length > 0);
    const empty = filtered.filter((column) => column.tickets.length === 0);
    return withTickets.length > 0 ? [...withTickets, ...empty] : filtered;
  }, [columns]);

  useEffect(() => {
    if (!visibleColumns.length) {
      setActiveStatusCode(null);
      return;
    }
    setActiveStatusCode((current) => {
      if (current && visibleColumns.some((column) => column.code === current)) {
        return current;
      }
      return visibleColumns[0].code;
    });
  }, [visibleColumns]);

  useEffect(() => {
    if (!isMobile || !activeStatusCode) return;
    const el = tabRefs.current[activeStatusCode];
    if (!el) return;
    el.scrollIntoView({ inline: 'center', behavior: 'smooth', block: 'nearest' });
  }, [activeStatusCode, isMobile]);

  const activeColumn = useMemo(
    () => visibleColumns.find((column) => column.code === activeStatusCode) || null,
    [visibleColumns, activeStatusCode]
  );

  const resetDragState = useCallback(() => {
    setDraggedTicketId(null);
    setDragOverColumn(null);
    window.setTimeout(() => {
      isDraggingRef.current = false;
    }, 0);
  }, []);

  const stopAutoScroll = useCallback(() => {
    autoScrollPointerRef.current.active = false;
    if (autoScrollRafRef.current != null) {
      window.cancelAnimationFrame(autoScrollRafRef.current);
      autoScrollRafRef.current = null;
    }
    const viewport = viewportRef.current;
    if (viewport) {
      viewport.style.scrollBehavior = '';
    }
  }, []);

  const tickAutoScroll = useCallback(() => {
    autoScrollRafRef.current = null;
    const pointer = autoScrollPointerRef.current;
    const viewport = viewportRef.current;
    if (!pointer.active || !viewport) return;

    const EDGE = 72;
    const MAX_SPEED = 22;
    const OUTSIDE_TOLERANCE = 24;
    const rect = viewport.getBoundingClientRect();
    let dx = 0;
    let dy = 0;

    const inXBand =
      pointer.x >= rect.left - OUTSIDE_TOLERANCE &&
      pointer.x <= rect.right + OUTSIDE_TOLERANCE;
    const inYBand =
      pointer.y >= rect.top - OUTSIDE_TOLERANCE &&
      pointer.y <= rect.bottom + OUTSIDE_TOLERANCE;

    if (inYBand) {
      const distLeft = pointer.x - rect.left;
      const distRight = rect.right - pointer.x;
      if (distLeft < EDGE) {
        dx = -MAX_SPEED * (1 - Math.max(distLeft, 0) / EDGE);
      } else if (distRight < EDGE) {
        dx = MAX_SPEED * (1 - Math.max(distRight, 0) / EDGE);
      }
    }

    const columnScroller = inXBand
      ? document.elementFromPoint(pointer.x, pointer.y)?.closest('[data-kanban-column-scroll]')
      : null;
    if (columnScroller) {
      const columnRect = columnScroller.getBoundingClientRect();
      const distTop = pointer.y - columnRect.top;
      const distBottom = columnRect.bottom - pointer.y;
      if (distTop < EDGE) {
        dy = -MAX_SPEED * (1 - Math.max(distTop, 0) / EDGE);
      } else if (distBottom < EDGE) {
        dy = MAX_SPEED * (1 - Math.max(distBottom, 0) / EDGE);
      }
    }

    if (dx === 0 && dy === 0) return;

    viewport.scrollLeft += dx;
    if (columnScroller && dy !== 0) {
      columnScroller.scrollTop += dy;
    }
    autoScrollRafRef.current = window.requestAnimationFrame(tickAutoScroll);
  }, []);

  const handleAutoScrollDragOver = useCallback(
    (event) => {
      autoScrollPointerRef.current.x = event.clientX;
      autoScrollPointerRef.current.y = event.clientY;
      if (!autoScrollPointerRef.current.active) {
        autoScrollPointerRef.current.active = true;
      }
      if (autoScrollRafRef.current == null) {
        autoScrollRafRef.current = window.requestAnimationFrame(tickAutoScroll);
      }
    },
    [tickAutoScroll]
  );

  const clearDragFlushTimer = useCallback(() => {
    if (dragFlushTimerRef.current != null) {
      window.clearTimeout(dragFlushTimerRef.current);
      dragFlushTimerRef.current = null;
    }
  }, []);

  const commitPendingMove = useCallback(async (pending, resolutionPayload = null) => {
    if (!pending?.ticketId || !pending.columnCode) return;
    const { ticketId, columnCode } = pending;
    setUpdatingTicketIds((prev) => new Set(prev).add(ticketId));
    try {
      await moveKanbanTicketStatus(ticketId, columnCode, resolutionPayload);
    } catch {
      // Toast e rollback tratados no AppContext
    } finally {
      setUpdatingTicketIds((prev) => {
        const next = new Set(prev);
        next.delete(ticketId);
        return next;
      });
    }
  }, [moveKanbanTicketStatus]);

  const detachDocDragEnd = useCallback(() => {
    if (!docDragEndRef.current) return;
    document.removeEventListener('dragend', docDragEndRef.current);
    docDragEndRef.current = null;
  }, []);

  const flushDragGesture = useCallback(() => {
    clearDragFlushTimer();
    detachDocDragEnd();
    stopAutoScroll();
    document.removeEventListener('dragover', handleAutoScrollDragOver);
    const pending = pendingMoveRef.current;
    pendingMoveRef.current = null;
    resetDragState();
    if (!pending) return;

    const sourceTicket = tickets.find((t) => t.id === pending.ticketId);
    const fromTerminal = isTerminalStatus(sourceTicket?.status);
    if (!fromTerminal && requiresResolutionCapture(pending.columnCode)) {
      setResolveModalPending(pending);
      return;
    }
    commitPendingMove(pending);
  }, [
    clearDragFlushTimer,
    detachDocDragEnd,
    stopAutoScroll,
    handleAutoScrollDragOver,
    resetDragState,
    commitPendingMove,
    tickets,
  ]);

  const handleResolveModalCancel = useCallback(() => {
    if (resolveSubmitting) return;
    setResolveModalPending(null);
  }, [resolveSubmitting]);

  const handleResolveModalConfirm = useCallback(async ({ resolutionType, resolutionNotes }) => {
    if (!resolveModalPending) return;
    setResolveSubmitting(true);
    try {
      await commitPendingMove(resolveModalPending, { resolutionType, resolutionNotes });
      setResolveModalPending(null);
    } finally {
      setResolveSubmitting(false);
    }
  }, [resolveModalPending, commitPendingMove]);

  const resolveModalTicket = useMemo(
    () => tickets.find((t) => t.id === resolveModalPending?.ticketId) || null,
    [tickets, resolveModalPending]
  );
  const handleDragStart = useCallback((event, ticket) => {
    if (!ticket?.id || updatingTicketIds.has(ticket.id)) {
      event.preventDefault();
      return;
    }
    clearDragFlushTimer();
    pendingMoveRef.current = null;
    isDraggingRef.current = true;
    setDraggedTicketId(ticket.id);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', ticket.id);
    event.dataTransfer.setData('application/x-ticket-status', String(ticket.status || ''));
    autoScrollPointerRef.current = {
      x: event.clientX,
      y: event.clientY,
      active: true,
    };
    if (viewportRef.current) {
      viewportRef.current.style.scrollBehavior = 'auto';
    }
    document.addEventListener('dragover', handleAutoScrollDragOver);
    if (autoScrollRafRef.current == null) {
      autoScrollRafRef.current = window.requestAnimationFrame(tickAutoScroll);
    }
    detachDocDragEnd();
    const onDocDragEnd = () => flushDragGesture();
    docDragEndRef.current = onDocDragEnd;
    document.addEventListener('dragend', onDocDragEnd);
  }, [
    updatingTicketIds,
    clearDragFlushTimer,
    handleAutoScrollDragOver,
    tickAutoScroll,
    detachDocDragEnd,
    flushDragGesture,
  ]);

  const handleDragEnd = useCallback(() => {
    flushDragGesture();
  }, [flushDragGesture]);

  const handleDragOver = useCallback((event, columnCode, ticket) => {
    if (columnCode === '_OTHER') return;
    if (!canDropTicketInColumn(ticket, columnCode)) {
      event.dataTransfer.dropEffect = 'none';
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnCode);
  }, []);

  const handleDragLeave = useCallback((event, columnCode) => {
    if (columnCode === '_OTHER') return;
    const related = event.relatedTarget;
    if (related && event.currentTarget.contains(related)) return;
    setDragOverColumn((current) => (current === columnCode ? null : current));
  }, []);

  const handleDrop = useCallback((event, columnCode) => {
    event.preventDefault();
    if (columnCode === '_OTHER') return;

    const ticketId = event.dataTransfer.getData('text/plain') || draggedTicketId;
    const ticket = tickets.find((t) => t.id === ticketId);
    if (!ticket?.id) return;
    if (String(ticket.status || '').toUpperCase() === columnCode) return;

    if (!canDropTicketInColumn(ticket, columnCode)) {
      const reopenBlocked =
        isTerminalStatus(ticket.status) && !isReopenTargetStatus(columnCode);
      addToast(
        reopenBlocked
          ? 'Ao reabrir, o status de destino deve ser de pipeline aberto (novo, em andamento ou pendente).'
          : `O status "${getStatusLabel(columnCode)}" não está disponível para tickets de ${getDemandTypeLabel(ticket.demandType)}.`,
        'error'
      );
      return;
    }

    // Não troca de coluna aqui: desmontar o card no meio do gesto prende o drag nativo.
    pendingMoveRef.current = { ticketId: ticket.id, columnCode };
    clearDragFlushTimer();
  }, [tickets, draggedTicketId, addToast, clearDragFlushTimer]);

  useEffect(() => () => {
    clearDragFlushTimer();
    detachDocDragEnd();
    stopAutoScroll();
    document.removeEventListener('dragover', handleAutoScrollDragOver);
  }, [clearDragFlushTimer, detachDocDragEnd, stopAutoScroll, handleAutoScrollDragOver]);

  const handleCardClick = useCallback((event) => {
    if (isDraggingRef.current) {
      event.preventDefault();
    }
  }, []);

  const goToAdjacentStatus = useCallback(
    (direction) => {
      if (!visibleColumns.length || !activeStatusCode) return;
      const idx = visibleColumns.findIndex((column) => column.code === activeStatusCode);
      if (idx < 0) return;
      const nextIdx = idx + direction;
      if (nextIdx < 0 || nextIdx >= visibleColumns.length) return;
      setActiveStatusCode(visibleColumns[nextIdx].code);
    },
    [visibleColumns, activeStatusCode]
  );

  const handleTabsPointerDown = useCallback((event) => {
    const el = tabsScrollRef.current;
    if (!el) return;
    el.setPointerCapture?.(event.pointerId);
    tabsPointerDragRef.current = {
      active: true,
      startX: event.clientX,
      scrollLeft: el.scrollLeft,
      moved: false,
    };
  }, []);

  const handleTabsPointerMove = useCallback((event) => {
    const drag = tabsPointerDragRef.current;
    const el = tabsScrollRef.current;
    if (!drag.active || !el) return;
    const dx = event.clientX - drag.startX;
    if (Math.abs(dx) > 4) drag.moved = true;
    el.scrollLeft = drag.scrollLeft - dx;
  }, []);

  const handleTabsPointerUp = useCallback((event) => {
    const el = tabsScrollRef.current;
    if (el && event?.pointerId != null) {
      try {
        el.releasePointerCapture?.(event.pointerId);
      } catch {
        // ignore
      }
    }
    tabsPointerDragRef.current.active = false;
  }, []);

  const handleTabsClickCapture = useCallback((event) => {
    if (tabsPointerDragRef.current.moved) {
      event.preventDefault();
      event.stopPropagation();
      tabsPointerDragRef.current.moved = false;
    }
  }, []);

  const handleListTouchStart = useCallback((event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    listSwipeRef.current = {
      active: true,
      startX: touch.clientX,
      startY: touch.clientY,
    };
  }, []);

  const handleListTouchEnd = useCallback(
    (event) => {
      const swipe = listSwipeRef.current;
      if (!swipe.active) return;
      listSwipeRef.current.active = false;
      const touch = event.changedTouches?.[0];
      if (!touch) return;
      const dx = touch.clientX - swipe.startX;
      const dy = touch.clientY - swipe.startY;
      const SWIPE_THRESHOLD = 56;
      if (Math.abs(dx) < SWIPE_THRESHOLD) return;
      if (Math.abs(dx) < Math.abs(dy) * 1.2) return; // prioriza scroll vertical
      goToAdjacentStatus(dx < 0 ? 1 : -1);
    },
    [goToAdjacentStatus]
  );

  if (loading && tickets.length === 0) {
    return <TicketKanbanSkeleton />;
  }

  if (!loading && tickets.length === 0) {
    return (
      <EmptyWrap>
        <Inbox size={24} strokeWidth={1.75} aria-hidden />
        Nenhum ticket encontrado com os filtros atuais.
      </EmptyWrap>
    );
  }

  const renderTicketCard = (ticket, columnCode, { draggable = false } = {}) => {
    const isUpdating = updatingTicketIds.has(ticket.id);
    const canDrag = draggable && !readOnly;

    return (
      <TicketCard
        key={ticket.id}
        ticket={ticket}
        users={users}
        to={`${detailsPath}/${ticket.id}`}
        linkState={{ returnTo: listReturnPath }}
        titleLines={3}
        showStatus={columnCode === '_OTHER'}
        draggable={canDrag}
        isDragging={draggedTicketId === ticket.id}
        isUpdating={isUpdating}
        onDragStart={canDrag ? (e) => handleDragStart(e, ticket) : undefined}
        onDragEnd={canDrag ? handleDragEnd : undefined}
        onClick={handleCardClick}
        onOpenComments={() =>
          navigate(`${detailsPath}/${ticket.id}#ticket-messages`, {
            state: { returnTo: listReturnPath },
          })
        }
      />
    );
  };

  if (isMobile) {
    return (
      <>
      <MobileKanban>
        <StatusTabs
          ref={tabsScrollRef}
          role="tablist"
          aria-label="Status do Kanban"
          onPointerDown={handleTabsPointerDown}
          onPointerMove={handleTabsPointerMove}
          onPointerUp={handleTabsPointerUp}
          onPointerCancel={handleTabsPointerUp}
          onPointerLeave={handleTabsPointerUp}
          onClickCapture={handleTabsClickCapture}
        >
          {visibleColumns.map((column) => (
            <StatusTab
              key={column.code}
              type="button"
              role="tab"
              ref={(el) => {
                tabRefs.current[column.code] = el;
              }}
              $active={column.code === activeStatusCode}
              aria-selected={column.code === activeStatusCode}
              onClick={() => setActiveStatusCode(column.code)}
            >
              {column.label}
              <StatusTabCount>{column.tickets.length}</StatusTabCount>
            </StatusTab>
          ))}
        </StatusTabs>

        <MobileTicketList
          onTouchStart={handleListTouchStart}
          onTouchEnd={handleListTouchEnd}
        >
          {activeColumn?.tickets?.length > 0 ? (
            activeColumn.tickets.map((ticket) =>
              renderTicketCard(ticket, activeColumn.code, { draggable: false })
            )
          ) : (
            <MobileEmpty>
              <Inbox size={20} strokeWidth={1.75} aria-hidden />
              Nenhum ticket neste status.
            </MobileEmpty>
          )}
        </MobileTicketList>
      </MobileKanban>
      <ResolveTicketModal
        open={Boolean(resolveModalPending)}
        requireResolutionType={(resolveModalTicket?.demandType || 'SUPPORT') === 'SUPPORT'}
        submitting={resolveSubmitting}
        targetStatusLabel={getStatusLabel(resolveModalPending?.columnCode) || ''}
        onCancel={handleResolveModalCancel}
        onConfirm={handleResolveModalConfirm}
      />
      </>
    );
  }

  return (
    <>
    <KanbanViewport ref={viewportRef}>
      <Board>
      {visibleColumns.map((column) => {
        const isDropTarget = !readOnly && column.code !== '_OTHER';
        const dropAllowed = draggedTicket ? canDropTicketInColumn(draggedTicket, column.code) : true;
        const isDragOver = isDropTarget && dragOverColumn === column.code && dropAllowed;
        const isInvalidDrop = Boolean(draggedTicket && isDropTarget && !dropAllowed);

        return (
          <Column
            key={column.code}
            aria-label={`${column.label}: ${column.tickets.length} tickets`}
            $isDragOver={isDragOver}
            $isInvalidDrop={isInvalidDrop}
          >
            <ColumnHeader>
              <StatusDot $status={getStatusClass(column.code)} aria-hidden />
              <ColumnTitle title={column.label}>{column.label}</ColumnTitle>
              <ColumnCount>{column.tickets.length}</ColumnCount>
            </ColumnHeader>
            <ColumnBody
              data-kanban-column-scroll=""
              $isDragOver={isDragOver}
              onDragOver={
                isDropTarget
                  ? (e) => handleDragOver(e, column.code, draggedTicket)
                  : undefined
              }
              onDragLeave={
                isDropTarget
                  ? (e) => handleDragLeave(e, column.code)
                  : undefined
              }
              onDrop={
                isDropTarget
                  ? (e) => handleDrop(e, column.code)
                  : undefined
              }
            >
              {column.tickets.map((ticket) =>
                renderTicketCard(ticket, column.code, { draggable: true })
              )}
              {column.tickets.length === 0 && (
                <ColumnEmpty $active={isDragOver}>
                  {isDragOver ? 'Soltar aqui' : 'Nenhum ticket'}
                </ColumnEmpty>
              )}
            </ColumnBody>
          </Column>
        );
      })}
      </Board>
    </KanbanViewport>
      <ResolveTicketModal
        open={Boolean(resolveModalPending)}
        requireResolutionType={(resolveModalTicket?.demandType || 'SUPPORT') === 'SUPPORT'}
        submitting={resolveSubmitting}
        targetStatusLabel={getStatusLabel(resolveModalPending?.columnCode) || ''}
        onCancel={handleResolveModalCancel}
        onConfirm={handleResolveModalConfirm}
      />
    </>
  );
};

export default TicketKanbanView;
